import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { httpClient } from "@/shared/api/http-client";
import type {
  AddTranslationInput,
  CreateTosInput,
  RetireTosInput,
  TermsOfService,
  TosRoleScope,
  TosTranslation,
  UpdateTranslationInput,
} from "../shared/compliance-types";
import { REQUIREMENTS_QUERY_KEY } from "../requirements/use-requirements";

export const DOCUMENTS_QUERY_KEY = ["compliance", "documents"] as const;
export const DOCUMENT_DETAIL_QUERY_KEY = ["compliance", "document"] as const;

interface DocumentsFilters {
  readonly roleScope?: TosRoleScope;
  readonly documentTypeId?: string;
}

async function fetchDocuments(filters: DocumentsFilters): Promise<readonly TermsOfService[]> {
  const params = new URLSearchParams();
  if (filters.roleScope) params.set("roleScope", filters.roleScope);
  if (filters.documentTypeId) params.set("documentTypeId", filters.documentTypeId);
  const query = params.toString();
  return httpClient<readonly TermsOfService[]>(`/api/admin/tos${query ? `?${query}` : ""}`);
}

export function useDocuments(filters: DocumentsFilters = {}) {
  return useQuery({
    queryKey: [
      ...DOCUMENTS_QUERY_KEY,
      filters.roleScope ?? "all",
      filters.documentTypeId ?? "all",
    ] as const,
    queryFn: () => fetchDocuments(filters),
    staleTime: 30_000,
  });
}

async function fetchDocumentDetail(id: string): Promise<TermsOfService> {
  return httpClient<TermsOfService>(`/api/admin/tos/${id}`);
}

export function useDocumentDetail(id: string | undefined) {
  return useQuery({
    queryKey: [...DOCUMENT_DETAIL_QUERY_KEY, id ?? "none"] as const,
    queryFn: () => fetchDocumentDetail(id as string),
    enabled: id != null && id.length > 0,
    staleTime: 30_000,
  });
}

async function createDocument(input: CreateTosInput): Promise<TermsOfService> {
  return httpClient<TermsOfService>("/api/admin/tos", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
}

export function useCreateDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createDocument,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEY });
    },
  });
}

async function publishDocument(id: string): Promise<TermsOfService> {
  return httpClient<TermsOfService>(`/api/admin/tos/${id}/publish`, {
    method: "POST",
  });
}

export function usePublishDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: publishDocument,
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEY });
      queryClient.invalidateQueries({
        queryKey: [...DOCUMENT_DETAIL_QUERY_KEY, id] as const,
      });
      // Publishing a new version may unblock pending role requirements;
      // refresh the requirements query so the RequirementsPage reflects
      // the new active set without a manual refetch.
      queryClient.invalidateQueries({ queryKey: REQUIREMENTS_QUERY_KEY });
    },
  });
}

interface RetireDocumentParams {
  readonly id: string;
  readonly input: RetireTosInput;
}

async function retireDocument({ id, input }: RetireDocumentParams): Promise<TermsOfService> {
  return httpClient<TermsOfService>(`/api/admin/tos/${id}/retire`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
}

export function useRetireDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: retireDocument,
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEY });
      queryClient.invalidateQueries({
        queryKey: [...DOCUMENT_DETAIL_QUERY_KEY, vars.id] as const,
      });
      // Retiring a doc may break a role-requirement (no active replacement
      // for this (role, type) anymore). Refresh requirements so the
      // RequirementsPage reflects the new eligible set.
      queryClient.invalidateQueries({ queryKey: REQUIREMENTS_QUERY_KEY });
    },
  });
}

interface AddTranslationParams {
  readonly tosId: string;
  readonly input: AddTranslationInput;
}

async function addTranslation({ tosId, input }: AddTranslationParams): Promise<TosTranslation> {
  return httpClient<TosTranslation>(`/api/admin/tos/${tosId}/translations`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
}

export function useAddTranslation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addTranslation,
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: [...DOCUMENT_DETAIL_QUERY_KEY, vars.tosId] as const,
      });
      queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEY });
    },
  });
}

interface UpdateTranslationParams {
  readonly tosId: string;
  readonly locale: string;
  readonly input: UpdateTranslationInput;
}

async function updateTranslation({
  tosId,
  locale,
  input,
}: UpdateTranslationParams): Promise<TosTranslation> {
  return httpClient<TosTranslation>(
    `/api/admin/tos/${tosId}/translations/${encodeURIComponent(locale)}`,
    {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input),
    },
  );
}

export function useUpdateTranslation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateTranslation,
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: [...DOCUMENT_DETAIL_QUERY_KEY, vars.tosId] as const,
      });
      queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEY });
    },
  });
}
